/**
 * Phase 5 of the Firebase Auth migration: API calls authenticate with our own
 * session instead of a Firebase ID token that the gateway exchanges at
 * /retrieve-jwt.
 *
 * Only a login whose api-users response carried the additive `session` field
 * (the `session-auth` flag admitted the organization) is on this path. Every
 * other user keeps sending the Firebase ID token exactly as before, and a user
 * whose organization is enabled mid-session stays on Firebase until the next
 * login. Firebase stays signed in either way (custom token), because RTDB and
 * Storage still need a Firebase user.
 *
 * Persistence: localStorage. Firebase persists its own refresh token for this
 * origin in IndexedDB (default LOCAL persistence) and survives reloads and
 * tabs; this credential has the same readers (script on this origin), the same
 * lifetime (until sign-out) and is no more powerful. It is removed on every
 * sign-out, and never logged.
 *
 * The flow mirrors audit-lite's session-token-manager-service: renew five
 * minutes before expiry, one refresh in flight per session, and a refused
 * refresh (400/401/403) ends the session; any other failure is transient and
 * leaves the session in place.
 */

import { captureException } from '@sentry/react';

export const SESSION_STORAGE_KEY = 'nimbly:session';
const RENEWAL_BUFFER_SECONDS = 5 * 60;
const FIREBASE_ISSUER = 'https://securetoken.google.com/';

export interface SessionCredentials {
	accessToken: string;
	refreshToken: string;
	tokenType: 'Bearer';
	/** Epoch seconds. */
	expiresAt: number;
	/** Owner of the session, so it can never authenticate another account. */
	userID: string;
}

export type Send = (url: string, options?: RequestInit) => Promise<Response>;

export class SessionRejectedError extends Error {
	constructor() {
		super('Session refresh was rejected');
		this.name = 'SessionRejectedError';
	}
}

const cloudApiUrl = (): string => import.meta.env.REACT_APP_CLOUD_API_URL || '';
// shortcut: failed storage leaves credentials tab-local until the next successful write or clear.
let storageUnavailable = false;
let memorySession: SessionCredentials | null = null;
let sessionVersion = 0;
let pendingLogin: SessionCredentials | null = null;
// Keep rejected sessions closed even if Firebase sign-out fails.
let sessionRejected = false;

const isCredentials = (value: any): value is SessionCredentials =>
	Boolean(value) &&
	typeof value.accessToken === 'string' &&
	value.accessToken !== '' &&
	typeof value.refreshToken === 'string' &&
	value.refreshToken !== '' &&
	value.tokenType === 'Bearer' &&
	Number.isFinite(value.expiresAt) &&
	typeof value.userID === 'string' &&
	value.userID !== '';

const credentialsFrom = (value: any, userID: string): SessionCredentials | null => {
	const candidate = {
		accessToken: value?.accessToken,
		refreshToken: value?.refreshToken,
		tokenType: value?.tokenType,
		expiresAt: value?.expiresAt,
		userID,
	};
	return isCredentials(candidate) ? candidate : null;
};

export const readSession = (): SessionCredentials | null => {
	if (storageUnavailable) {
		return memorySession;
	}

	let raw: string | null;
	try {
		raw = localStorage.getItem(SESSION_STORAGE_KEY);
	} catch {
		storageUnavailable = true;
		return memorySession;
	}

	try {
		const stored = JSON.parse(raw || 'null');
		if (stored?.rejected === true) sessionRejected = true;
		memorySession = isCredentials(stored) ? stored : null;
		return memorySession;
	} catch {
		// Malformed persisted data is not a storage outage. A later login can
		// clear and replace it, while this call fails closed.
		memorySession = null;
		return null;
	}
};

const writeSession = (credentials: SessionCredentials): void => {
	// Keep the live credential in memory when quota or privacy settings reject
	// the write. Future reads must not fall back to an older disk credential.
	memorySession = credentials;
	try {
		localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(credentials));
		storageUnavailable = false;
	} catch {
		storageUnavailable = true;
	}
};

export const clearSession = (rejected = false): void => {
	sessionVersion += 1;
	pendingLogin = null;
	memorySession = null;
	try {
		// Replace rejected credentials in one write, without a remove/set window for another login.
		if (rejected) localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ rejected: true }));
		else localStorage.removeItem(SESSION_STORAGE_KEY);
		storageUnavailable = false;
	} catch {
		// Ignore the old disk value until an explicit login can replace it.
		storageUnavailable = true;
	}
};

/** Store the login's session; return the completion callback for its Firebase handshake. */
export const storeLoginSession = (loginData: any): ((succeeded?: boolean) => void) => {
	clearSession();
	if (loginData?.user?.userID) sessionRejected = false;
	const credentials = credentialsFrom(loginData?.session, loginData?.user?.userID);
	pendingLogin = credentials;
	if (credentials) {
		writeSession(credentials);
	}
	return (succeeded = true) => {
		if (!succeeded && isSameSession(readSession(), credentials)) clearSession();
		if (pendingLogin === credentials) pendingLogin = null;
	};
};

export const isSessionLoginPending = (): boolean => Boolean(pendingLogin && isSameSession(readSession(), pendingLogin));

const authorizationOf = (credentials: SessionCredentials): string =>
	`${credentials.tokenType} ${credentials.accessToken}`;

// Dynamic so the request layer does not import the store at module load.
const signOut = (version: number): void => {
	import('store/configStore')
		.then(({ auth }) => {
			if (sessionVersion === version && !readSession()) return auth().signOut();
		})
		.catch((error) =>
			captureException(new Error('Firebase sign-out failed after session rejection', { cause: error })),
		);
};

const sessionIDOf = (refreshToken: string): string => refreshToken.split('.', 1)[0];
const sessionKey = (credentials: SessionCredentials): string =>
	`${credentials.userID}:${sessionIDOf(credentials.refreshToken)}`;
export const isSameSession = (left: SessionCredentials | null, right: SessionCredentials | null): boolean =>
	Boolean(left && right && sessionKey(left) === sessionKey(right));

let inFlight: { key: string; promise: Promise<string> } | null = null;

const refresh = async (send: Send, presented: SessionCredentials): Promise<string> => {
	let response: Response;
	try {
		response = await send(`${cloudApiUrl()}/users/session/refresh`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ refreshToken: presented.refreshToken }),
		});
	} catch (error) {
		throw new Error('Session refresh request failed', { cause: error });
	}

	if ([400, 401, 403].includes(response.status)) {
		// A concurrent tab may already hold a newer credential for this same session.
		// Keep that live pair instead of logging every tab out because this request
		// presented the stale refresh secret.
		const latest = readSession();
		if (latest && isSameSession(latest, presented)) {
			if (latest.refreshToken !== presented.refreshToken) return authorizationOf(latest);
			sessionRejected = true;
			clearSession(true);
			signOut(sessionVersion);
		}
		throw new SessionRejectedError();
	}

	const body = response.ok ? await response.json().catch(() => null) : null;
	const rotated = credentialsFrom(body?.data, presented.userID);
	if (!rotated) {
		throw new Error(`Session refresh did not return credentials (status ${response.status})`);
	}

	const latest = readSession();
	if (!latest || !isSameSession(latest, presented)) {
		// Signed out or replaced while the refresh was in flight.
		throw new SessionRejectedError();
	}
	// Another tab may have rotated meanwhile; both pairs are valid inside the server's grace window.
	if (latest.refreshToken === presented.refreshToken) {
		writeSession(rotated);
	}
	const live = readSession();
	if (!live || !isSameSession(live, presented)) {
		throw new SessionRejectedError();
	}
	return authorizationOf(live);
};

/**
 * `Bearer <access token>` for the stored session, renewed shortly before it
 * expires (or now, when forced). Null when this login is not on the session path.
 */
export const getSessionAuthorization = async (
	send: Send,
	forceRefresh = false,
	expected?: SessionCredentials,
): Promise<string | null> => {
	const credentials = readSession();
	if (!credentials && sessionRejected) throw new SessionRejectedError();
	if (!credentials || (expected && !isSameSession(credentials, expected))) {
		return null;
	}
	if (!forceRefresh && credentials.expiresAt - Date.now() / 1000 > RENEWAL_BUFFER_SECONDS) {
		return authorizationOf(credentials);
	}
	const key = sessionKey(credentials);
	if (!inFlight || inFlight.key !== key) {
		let promise: Promise<string>;
		promise = refresh(send, credentials).finally(() => {
			if (inFlight?.promise === promise) inFlight = null;
		});
		inFlight = { key, promise };
	}
	return inFlight.promise;
};

const isFirebaseIdToken = (authorization: string, userID: string): boolean => {
	try {
		const payload = authorization.replace(/^Bearer\s+/i, '').split('.')[1];
		const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
		return (
			typeof claims.iss === 'string' &&
			claims.iss.startsWith(FIREBASE_ISSUER) &&
			(claims.user_id === userID || claims.sub === userID)
		);
	} catch {
		return false;
	}
};

const isCloudApiUrl = (url: string): boolean => {
	const cloud = cloudApiUrl();
	if (!cloud) return false;
	try {
		const base = new URL(cloud);
		const target = new URL(url);
		const basePath = base.pathname.replace(/\/+$/, '') || '/';
		return (
			target.origin === base.origin &&
			(basePath === '/' || target.pathname === basePath || target.pathname.startsWith(`${basePath}/`))
		);
	} catch {
		return false;
	}
};

/**
 * The session credential for a request to `url`, so callers that used to
 * exchange a Firebase token at /retrieve-jwt can skip it. Null when there is
 * no session, or when `url` bypasses the gateway: backends reached directly
 * only verify internal JWTs. Like sendWithSession, only for the account the
 * caller's Firebase token belongs to, so a session left by another login is
 * never used in that token's place.
 */
export const getSessionAuthorizationFor = (url: string, firebaseToken: string, send: Send): Promise<string | null> => {
	const session = readSession();
	if (!isCloudApiUrl(url) || !session || !isFirebaseIdToken(firebaseToken, session.userID)) {
		return Promise.resolve(null);
	}
	return getSessionAuthorization(send, false, session);
};

const withAuthorization = (options: RequestInit | undefined, authorization: string): RequestInit => {
	const headers = new Headers(options?.headers);
	headers.set('authorization', authorization);
	return { ...options, headers };
};

/**
 * Send a request, replacing a Firebase ID token with the session credential
 * when the request goes to the gateway. Anything else - no session, another
 * host, an internal JWT, no token - is sent exactly as given.
 *
 * A 401 renews the session once (a refused renewal signs out) and retries a
 * GET or HEAD once. Other methods return the 401: replaying a write is not
 * this layer's call, and the next request already carries the new credential.
 */
export const sendWithSession = async (url: string, options: RequestInit | undefined, send: Send): Promise<Response> => {
	const session = readSession();
	const isGateway = isCloudApiUrl(url);
	if (!session && sessionRejected && isGateway && new Headers(options?.headers).has('authorization')) {
		throw new SessionRejectedError();
	}
	if (!session || !isGateway) {
		return send(url, options);
	}
	const original = new Headers(options?.headers).get('authorization');
	if (!original || !isFirebaseIdToken(original, session.userID)) {
		return send(url, options);
	}

	const authorization = await getSessionAuthorization(send, false, session);
	if (!authorization || !isSameSession(readSession(), session)) {
		throw new SessionRejectedError();
	}

	const response = await send(url, withAuthorization(options, authorization));
	if (response.status !== 401) {
		return response;
	}
	const replayable = ['GET', 'HEAD'].includes((options?.method || 'GET').toUpperCase());

	// A request that raced a renewal just retries with the newer credential.
	const current = readSession();
	const renewed = isSameSession(current, session)
		? current && authorizationOf(current) !== authorization
			? authorizationOf(current)
			: await getSessionAuthorization(send, true, session).catch(() => null)
		: null;
	const live = readSession();
	return renewed && replayable && isSameSession(live, session)
		? send(url, withAuthorization(options, renewed))
		: response;
};

/** On sign-out: forget the session here first, then revoke it server-side (best effort). */
export const endSession = (send: Send, expected?: SessionCredentials | null): void => {
	const credentials = readSession();
	if (credentials && ((expected !== undefined && !expected) || (expected && !isSameSession(credentials, expected)))) {
		return;
	}
	clearSession();
	sessionRejected = false;
	if (!credentials) return;
	send(`${cloudApiUrl()}/users/session/revoke`, {
		method: 'POST',
		keepalive: true,
		headers: { authorization: authorizationOf(credentials) },
	}).catch(() => undefined);
};
